/**
 * Локальное сохранение свободной сцены.
 *
 * Хранилище браузера — удобство, а не источник истины: другой браузер, режим
 * инкогнито и очистка данных законно дают пустоту. Поэтому любое чтение,
 * которое не проходит проверку, молча возвращает `y = x` вместо ошибки —
 * ученик, открывший «График», обязан увидеть плоскость, а не сообщение.
 */

import {
  FAMILY_NAMES, MAX_CURVES, defaultCurve, newScene, parametersOf, setParameter,
  type CurveFamily, type FunctionCurve, type LabScene, type SceneCurve,
} from "./model";
import { clampScale, type PlotCamera } from "./viewport";
import type { ExactNumber } from "./numbers";

const STORAGE_KEY = "traektoria.function-lab.scene";
const VERSION = 1;

type Bag = Record<string, unknown>;

function bag(value: unknown): Bag | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Bag)
    : null;
}

function readExact(value: unknown): ExactNumber | null {
  const raw = bag(value);
  if (!raw) return null;
  const numerator = raw.numerator;
  const denominator = raw.denominator;
  if (typeof numerator !== "number" || typeof denominator !== "number") return null;
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator)) return null;
  if (denominator <= 0) return null;
  return { numerator, denominator };
}

/**
 * Кривая из снимка. Коэффициенты читаются по списку параметров семейства —
 * том же, по которому строится карточка, — а не отдельной веткой на каждое
 * семейство: новое семейство тогда не может забыть попасть в сохранение.
 */
function readCurve(value: unknown): FunctionCurve | null {
  const raw = bag(value);
  if (!raw) return null;
  if (typeof raw.kind !== "string" || !Object.hasOwn(FAMILY_NAMES, raw.kind)) return null;
  const family = raw.kind as CurveFamily;
  if (family === "parabola" && !bag(raw.b) && bag(raw.h)) {
    // Снимок до разделения записей: парабола хранилась как a, h, k. Это та же
    // кривая, просто в виде с вершиной, — читаем ею, а не выбрасываем сцену.
    return readParameters("vertex", raw);
  }
  return readParameters(family, raw);
}

function readParameters(family: CurveFamily, raw: Bag): FunctionCurve | null {
  let curve = defaultCurve(family);
  for (const { key } of parametersOf(family)) {
    const value = readExact(raw[key]);
    if (!value) return null;
    curve = setParameter(curve, key, value);
  }
  return curve;
}

function readSceneCurve(value: unknown): SceneCurve | null {
  const raw = bag(value);
  if (!raw) return null;
  const curve = readCurve(raw.curve);
  if (!curve) return null;
  if (typeof raw.id !== "string" || raw.id === "") return null;
  if (typeof raw.slot !== "number" || !Number.isInteger(raw.slot)) return null;
  if (raw.slot < 0 || raw.slot >= MAX_CURVES) return null;
  if (typeof raw.visible !== "boolean") return null;
  return { id: raw.id, slot: raw.slot, visible: raw.visible, curve };
}

function readCamera(value: unknown): PlotCamera | null {
  const raw = bag(value);
  if (!raw) return null;
  const { cx, cy, scale } = raw;
  if (typeof cx !== "number" || typeof cy !== "number" || typeof scale !== "number") return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(scale)) return null;
  return { cx, cy, scale: clampScale(scale) };
}

/** Разбор сохранённого снимка. `null` — снимок не годится, берём `y = x`. */
export function decodeScene(value: unknown): LabScene | null {
  const raw = bag(value);
  if (!raw) return null;
  if (raw.version !== VERSION) return null;
  if (!Array.isArray(raw.curves)) return null;
  if (raw.curves.length === 0 || raw.curves.length > MAX_CURVES) return null;

  const curves: SceneCurve[] = [];
  for (const item of raw.curves) {
    const curve = readSceneCurve(item);
    if (!curve) return null;
    if (curves.some((other) => other.id === curve.id || other.slot === curve.slot)) return null;
    curves.push(curve);
  }

  const camera = readCamera(raw.camera);
  if (!camera) return null;
  const activeId = typeof raw.activeId === "string" ? raw.activeId : "";
  if (!curves.some((curve) => curve.id === activeId)) return null;

  return { curves, activeId, camera, tableOpen: raw.tableOpen !== false };
}

export function encodeScene(scene: LabScene): unknown {
  return { version: VERSION, ...scene };
}

function store(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadScene(storage: Storage | null = store()): LabScene {
  if (!storage) return newScene();
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return newScene();
    return decodeScene(JSON.parse(raw) as unknown) ?? newScene();
  } catch {
    return newScene();
  }
}

export function saveScene(scene: LabScene, storage: Storage | null = store()): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(encodeScene(scene)));
  } catch {
    // Квота или запрет на запись — сцена живёт в памяти до конца сессии.
  }
}
